import * as React from "react";
import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { useFormikContext } from "formik";
import { availableTimeRangeTypes } from "views/Constants";
import { Tab, Tabs } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { isTimeRange } from "views/typeGuards/timeRange";
import migrateTimeRangeToNewType from "views/components/time-range-picker/migrateTimeRangeToNewType";
import useUserDateTime from "hooks/useUserDateTime";
import { toDateObject } from "util/DateTime";
import { RELATIVE_CLASSIFIED_ALL_TIME_RANGE } from "views/components/time-range-picker/RelativeTimeRangeClassifiedHelper";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import TabAbsoluteTimeRange from "./TabAbsoluteTimeRange";
import TabKeywordTimeRange from "./TabKeywordTimeRange";
import TabRelativeTimeRange from "./TabRelativeTimeRange";
import TabDisabledTimeRange from "./TabDisabledTimeRange";
export const timeRangePickerTabs = {
  absolute: TabAbsoluteTimeRange,
  relative: TabRelativeTimeRange,
  keyword: TabKeywordTimeRange
};
const StyledTabs = styled(Tabs)`
  margin-top: 1px;
  margin-bottom: 9px;
`;
const timeRangeTypeTabs = ({ activeTab, limitDuration, tabs }) => availableTimeRangeTypes.filter(({ type }) => tabs.includes(type)).map(({ type, name }) => {
  const TimeRangeTypeTab = timeRangePickerTabs[type];
  return /* @__PURE__ */ React.createElement(Tab, { title: name, key: `time-range-type-selector-${type}`, eventKey: type }, type === activeTab && /* @__PURE__ */ React.createElement(TimeRangeTypeTab, { disabled: false, limitDuration }));
});
const createDefaultRanges = (formatTime) => ({
  absolute: {
    type: "absolute",
    from: formatTime(toDateObject(/* @__PURE__ */ new Date()).subtract(300, "seconds"), "complete"),
    to: formatTime(toDateObject(/* @__PURE__ */ new Date()), "complete")
  },
  relative: {
    type: "relative",
    from: {
      value: 5,
      unit: "minutes",
      isAllTime: false
    },
    to: RELATIVE_CLASSIFIED_ALL_TIME_RANGE
  },
  keyword: {
    type: "keyword",
    keyword: "Last five minutes"
  },
  disabled: void 0
});
const newTabTimeRange = ({
  activeTab,
  nextTab,
  timeRangeTabs,
  formatTime,
  defaultRanges,
  userTimezone
}) => {
  if (timeRangeTabs[nextTab]) {
    return timeRangeTabs[nextTab];
  }
  if (isTimeRange(timeRangeTabs[activeTab])) {
    return migrateTimeRangeToNewType({
      oldTimeRange: timeRangeTabs[activeTab],
      type: nextTab,
      formatTime,
      userTimezone
    });
  }
  return defaultRanges[nextTab];
};
const TimeRangeTabs = ({ limitDuration, validTypes }) => {
  const sendTelemetry = useSendTelemetry();
  const { formatTime, userTimezone } = useUserDateTime();
  const {
    setValues,
    values: { activeTab, timeRangeTabs }
  } = useFormikContext();
  const defaultRanges = useMemo(() => createDefaultRanges(formatTime), [formatTime]);
  const onSelect = useCallback(
    (nextTab) => {
      setValues({
        timeRangeTabs: {
          ...timeRangeTabs,
          [nextTab]: newTabTimeRange({
            activeTab,
            nextTab,
            timeRangeTabs,
            formatTime,
            defaultRanges,
            userTimezone
          })
        },
        activeTab: nextTab
      });
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_TIMERANGE_PICKER_TAB_SELECTED, {
        app_section: "search-bar",
        app_action_value: "search-time-range",
        event_details: {
          tab: nextTab
        }
      });
    },
    [activeTab, defaultRanges, formatTime, sendTelemetry, setValues, timeRangeTabs, userTimezone]
  );
  const tabs = useMemo(
    () => timeRangeTypeTabs({
      activeTab,
      limitDuration,
      tabs: validTypes
    }),
    [activeTab, limitDuration, validTypes]
  );
  return /* @__PURE__ */ React.createElement(
    StyledTabs,
    {
      id: "dateTimeTypes",
      defaultActiveKey: availableTimeRangeTypes[0].type,
      activeKey: activeTab != null ? activeTab : -1,
      onSelect,
      animation: false
    },
    tabs,
    !activeTab && /* @__PURE__ */ React.createElement(TabDisabledTimeRange, null)
  );
};
export default TimeRangeTabs;
